/**
 * Atlas Web API client — ky-based fetch wrapper.
 *
 * - Base URL from VITE_API_BASE_URL (falls back to the same-origin /v1).
 * - Adds Authorization: Bearer <atlas-jwt> from the auth store.
 * - Sends Idempotency-Key for mutating calls that carry one.
 * - Unwraps the standard success envelope ({ data, meta }).
 * - Maps RFC 9457 problem+json errors to typed ApiError with
 *   `code`, `status`, `errors[]` (field-level), and requestId.
 * - Propagates Idempotency-Replay header so the UI can show replays.
 * - Renews an expired access token once and sends the request again
 *   (401 TOKEN_EXPIRED / TOKEN_INVALID; lib/auth/refresh.ts).
 * - On 401 SESSION_REVOKED, refreshes once: another tab may have signed the
 *   same person in again, and this tab then follows that session (D1042).
 */
import ky, { type KyInstance, type Options } from "ky";
import { useAuthStore } from "@/lib/auth/store";
import { SESSION_MESSAGES } from "@/lib/auth/messages";
// A cycle by design: refresh.ts sends POST /auth/refresh through post()
// below, and this client asks it to renew. Neither module touches the other
// while loading (only inside functions), which is all ES modules need.
import { renewFor } from "@/lib/auth/refresh";

export interface ApiErrorBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  requestId?: string;
  correlationId?: string;
  errors?: Array<{
    field: string;
    code: string;
    message: string;
    path?: string;
  }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly requestId?: string;
  readonly fieldErrors: ApiErrorBody["errors"];
  readonly replayed: boolean;
  /**
   * The whole problem body, extension members included (RFC 9457): a refusal
   * can carry what the caller needs to recover, such as the legal entities
   * to choose from with 422 LEGAL_ENTITY_REQUIRED.
   */
  readonly extensions: Readonly<Record<string, unknown>>;

  constructor(body: ApiErrorBody, status: number, replayed = false) {
    super(body.detail ?? body.title ?? `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "UNKNOWN";
    this.detail = body.detail ?? "";
    this.requestId = body.requestId;
    this.fieldErrors = body.errors;
    this.replayed = replayed;
    this.extensions = { ...(body as Record<string, unknown>) };
  }
}

const BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:8080");

/**
 * Normalize the base URL so the `/v1` version prefix is never doubled:
 * VITE_API_BASE_URL may be a bare origin (`http://host`), a relative root
 * (`/v1` via the nginx proxy), or already include the version segment.
 */
function normalizeBaseUrl(base: string): string {
  return base.replace(/\/+$/, "").replace(/\/v1$/i, "");
}

const API_VERSION = "/v1";

/**
 * Canonical path mapping — aligns frontend page calls with the backend's
 * API contract (Technical Specification §2), which serves module-agnostic
 * canonical routes (`/v1/sales-orders`, `/v1/accounts`, `/v1/products`).
 * Pages may reference friendly module-scoped paths; this layer rewrites
 * them to the routes the cell API actually exposes, so every call lands on
 * a real handler. Longest-prefix match wins; unmatched paths pass through.
 */
const CANONICAL_PATH_ALIASES: Array<[prefix: string, canonical: string]> = [
  // Finance
  ["/finance/journal-entries", "/journal-entries"],
  ["/finance/general-ledger", "/general-ledger"],
  ["/finance/trial-balance", "/trial-balance"],
  ["/finance/exchange-rates", "/exchange-rates"],
  ["/finance/fixed-assets", "/fixed-assets"],
  ["/finance/payments", "/payments"],
  ["/finance/supplier-invoices", "/supplier-invoices"],
  ["/finance/customer-invoices", "/sales-invoices"],
  ["/finance/bank-reconciliation", "/bank-reconciliation/candidates"],
  ["/finance/budgeting", "/budgets"],
  ["/finance/consolidation/groups", "/consolidation/groups"],
  [
    "/finance/intercompany/open-items",
    "/consolidation/intercompany/open-items",
  ],
  ["/finance/accounts", "/accounts"],
  // Sales / CRM
  ["/sales/leads", "/crm/leads"],
  ["/sales/opportunities", "/crm/opportunities"],
  ["/sales/price-lists", "/price-lists"],
  ["/sales/discounts", "/discounts"],
  ["/sales/invoices", "/sales-invoices"],
  ["/sales/customers", "/customers"],
  ["/sales/orders", "/sales-orders"],
  // Procurement
  ["/procurement/suppliers", "/suppliers"],
  ["/procurement/purchase-orders", "/purchase-orders"],
  ["/procurement/goods-receipts", "/goods-receipts"],
  ["/procurement/supplier-invoices", "/supplier-invoices"],
  ["/procurement/contracts", "/contracts"],
  // Inventory
  ["/inventory/cycle-counts", "/inventory-movements?movement_type=CycleCount"],
  ["/inventory/transfers", "/inventory-movements?movement_type=Transfer"],
  ["/inventory/replenishment/recommendations", "/inventory-balances"],
  ["/inventory/valuation", "/inventory-balances"],
  ["/inventory/traceability", "/lots?trace=true"],
  ["/inventory/lots", "/lots"],
  ["/inventory/balances", "/inventory-balances"],
  ["/inventory/movements", "/inventory-movements"],
  ["/inventory/stock", "/inventory-balances"],
  ["/inventory/products", "/products"],
  // Manufacturing
  ["/manufacturing/mrp/runs", "/planning/mrp/runs"],
  ["/manufacturing/quality-inspections", "/quality/inspections"],
  ["/manufacturing/work-centers", "/work-centers"],
  ["/manufacturing/production-orders", "/production-orders"],
  ["/manufacturing/boms", "/boms"],
  // Admin / config / governance
  ["/admin/api-clients", "/api-clients"],
  ["/admin/roles", "/roles"],
  ["/admin/users", "/users"],
  ["/config/entities", "/entity-definitions"],
  ["/config/layouts", "/layouts"],
  ["/config/views", "/views"],
  ["/integrations/webhooks", "/webhook-subscriptions"],
  ["/integrations/dead-letter", "/outbox-messages"],
  ["/audit/security", "/audit-logs"],
  ["/implementation/readiness", "/health/tenant"],
  ["/service/appointments", "/service/field-service"],
];

/** Rewrite a module-scoped path to its canonical backend route. */
export function canonicalizePath(path: string): string {
  // Split query string so prefix matching only considers the path portion.
  const qIndex = path.indexOf("?");
  const pathOnly = qIndex === -1 ? path : path.slice(0, qIndex);
  const query = qIndex === -1 ? "" : path.slice(qIndex);

  let best: string | null = null;
  let bestLen = -1;
  for (const [prefix, canonical] of CANONICAL_PATH_ALIASES) {
    if (pathOnly === prefix || pathOnly.startsWith(`${prefix}/`)) {
      if (prefix.length > bestLen) {
        best = canonical;
        bestLen = prefix.length;
      }
    }
  }
  if (best === null) return path;

  const rest = pathOnly.slice(bestLen);
  // Preserve an existing explicit query; else append the alias's query.
  const canonicalQuery = best.includes("?")
    ? best.slice(best.indexOf("?") + 1)
    : "";
  const base = best.includes("?") ? best.slice(0, best.indexOf("?")) : best;
  const parts = [query || (canonicalQuery ? `?${canonicalQuery}` : "")].filter(
    Boolean,
  );
  return `${base}${rest}${parts.join("")}`;
}

/**
 * The path form ky accepts alongside `prefixUrl`.
 *
 * ky throws "`input` must not begin with a slash when using `prefixUrl`" for a
 * leading slash, and every caller in this app writes paths as `/sales-orders`.
 * That made EVERY request throw before it was sent, so canonicalisation and the
 * slash strip happen together, once, here.
 */
function toRequestPath(path: string): string {
  return canonicalizePath(path).replace(/^\/+/, "");
}

/**
 * The one place the SPA reacts to the session gate (apps/api
 * shared/guards/session-gate.ts), so no page has to:
 *
 * - 401 SESSION_REVOKED: the server ended this session (signed out elsewhere,
 *   revoked, expired, or replaced by a new sign-in in this browser). The
 *   afterResponse hook below has already asked the refresh cookie whether
 *   this browser holds the same person's newer session, and when it did
 *   this tab is on that session now and this reply is to an older token
 *   (D1042). Otherwise clear it and leave a notice; RequireAuth sees the
 *   missing token and sends the user to /login.
 * - 403 TENANT_SUSPENDED / TENANT_NOT_ACTIVE: the workspace is unavailable.
 *   Record it; RequireAuth renders the full-page explanation instead of a
 *   screen of per-widget errors.
 *
 * Only a response to the CURRENT token counts. A request still in flight from
 * a session that has since been replaced must not end the new one. Sign-out
 * and refresh handle their own outcomes (lib/auth/sign-out.ts,
 * lib/auth/refresh.ts), so they are skipped here.
 *
 * An expired token never reaches this point while the session can be
 * renewed: the afterResponse hook below renews it and sends the request
 * again first.
 */
export function applySessionPolicy(err: ApiError, request: Request): void {
  const state = useAuthStore.getState();
  if (!state.accessToken) return;
  if (bearerOf(request) !== state.accessToken) return;
  if (isSessionRoute(request)) return;

  if (err.status === 401 && err.code === "SESSION_REVOKED") {
    state.endSession(SESSION_MESSAGES.sessionEnded);
  } else if (
    err.status === 403 &&
    (err.code === "TENANT_SUSPENDED" || err.code === "TENANT_NOT_ACTIVE")
  ) {
    state.setWorkspaceBlock({ code: err.code, detail: err.detail });
  }
}

/** The bearer token a request carried, or null. */
function bearerOf(request: Request): string | null {
  const header = request.headers.get("Authorization");
  return header?.startsWith("Bearer ") ? header.slice("Bearer ".length) : null;
}

/**
 * POST /auth/refresh and /auth/logout: the session's own routes. Their
 * answers are decided by refresh.ts and sign-out.ts. Renewing after one of
 * them would wait on the very refresh that is answering.
 */
function isSessionRoute(request: Request): boolean {
  const path = new URL(request.url).pathname;
  return path.endsWith("/auth/refresh") || path.endsWith("/auth/logout");
}

/**
 * POST /auth/login and /auth/mfa/verify: they run inside the refresh lock
 * (whileSigningIn, lib/api/sign-in.ts), so a renewal from their answer
 * would wait on the lock they hold. They are public and answer no token
 * refusal; this keeps it that way on the client too.
 */
function isSignInRoute(request: Request): boolean {
  const path = new URL(request.url).pathname;
  return path.endsWith("/auth/login") || path.endsWith("/auth/mfa/verify");
}

/** The refusals an access token gets that a refresh can cure. */
const RENEWABLE = new Set(["TOKEN_EXPIRED", "TOKEN_INVALID"]);

/**
 * The refusal that ends this token's session, after which the refresh
 * cookie may still name a NEWER session of the same person: a sign-in in
 * another tab ends the session the shared cookie named and leaves its own
 * in the jar (D1042). One refresh finds out. Adopting it, or ending this
 * tab's session when it is refused, is refresh.ts's decision, made exactly
 * as for any refresh. The request is never sent again: it was made under
 * the session that ended.
 */
const FOLLOWABLE = new Set(["SESSION_REVOKED"]);

const client: KyInstance = ky.create({
  // normalizeBaseUrl strips a trailing slash and an existing /v1 segment, so a
  // VITE_API_BASE_URL of `http://host/v1` (which is what the nginx proxy config
  // hands the SPA) cannot produce `/v1/v1/...`. The helper existed for exactly
  // this and was never called.
  prefixUrl: `${normalizeBaseUrl(BASE_URL)}${API_VERSION}`,
  timeout: 60_000,
  // Nothing is retried automatically (no method, no status). The one retry
  // allowed is the one the afterResponse hook forces after renewing an
  // expired token; ky counts a forced retry against this limit, so 0 would
  // turn it into an error.
  retry: { limit: 1, methods: [], statusCodes: [] },
  hooks: {
    beforeRequest: [
      (request) => {
        // On the retry after a renewal this sets the NEW token.
        //
        // A sign-out may name the session it ends (D1041): the forced
        // sign-out after creating a workspace ends the session POST
        // /v1/tenants opened, whose token is not the one in the store. Only
        // the session routes keep a bearer a caller set; they are never
        // retried, and every other route always carries the store's token.
        const token = useAuthStore.getState().accessToken;
        const named =
          isSessionRoute(request) && request.headers.has("Authorization");
        if (token && !named)
          request.headers.set("Authorization", `Bearer ${token}`);
        const idem = useAuthStore.getState().pendingIdempotencyKey;
        // A retried request keeps the key it was first sent with, and a
        // refresh or sign-out sent from under a form never takes the form's
        // key: either would leave the form's own request without it.
        if (
          idem &&
          !request.headers.has("Idempotency-Key") &&
          !isSessionRoute(request)
        ) {
          request.headers.set("Idempotency-Key", idem);
          useAuthStore.getState().clearPendingIdempotencyKey();
        }
        request.headers.set(
          "X-Tenant-Code",
          useAuthStore.getState().activeTenantCode ?? "",
        );
      },
    ],
    afterResponse: [
      /**
       * An access token lives 900 seconds. When one has expired (or was
       * signed with a key the server no longer holds), renew it once and
       * send the same request again, body and Idempotency-Key included, with
       * the new token. Only a request made under this tab's session is sent
       * again (renewFor). If the session cannot be renewed, refresh.ts has
       * already ended it, and this 401 reaches the caller as it is.
       *
       * A session that ended (SESSION_REVOKED) is refreshed once and never
       * sent again (FOLLOWABLE): the tab follows the same person's newer
       * session if the cookie names one, and is signed out otherwise.
       */
      async (request, _options, response, { retryCount }) => {
        if (response.status !== 401 || retryCount > 0) return;
        if (isSessionRoute(request) || isSignInRoute(request)) return;
        const sent = bearerOf(request);
        if (!sent) return;
        let code: string | undefined;
        try {
          code = ((await response.json()) as ApiErrorBody).code;
        } catch {
          return;
        }
        if (!code) return;
        if (FOLLOWABLE.has(code)) {
          await renewFor(sent);
          return;
        }
        if (!RENEWABLE.has(code)) return;
        if (!(await renewFor(sent))) return;
        return ky.retry({ delay: 0, code: "TOKEN_RENEWED" });
      },
    ],
    beforeError: [
      /**
       * Turn the API's problem+json into an ApiError.
       *
       * Without this ky throws its own HTTPError, which toApiError() does not
       * recognise, so EVERY failed request reached the UI as
       * code 'NETWORK_ERROR' with status 0 — a 422 listing exactly which
       * fields were wrong, or a 403 naming the missing permission, both
       * displayed as "Network error". The API goes to some trouble to return
       * that detail; this is what stops the client throwing it away.
       */
      async (error) => {
        const { response } = error;
        if (!response) return error;
        let body: ApiErrorBody = {};
        try {
          body = (await response.clone().json()) as ApiErrorBody;
        } catch {
          // Not JSON (a proxy error page, an empty body): fall back to the
          // status alone rather than losing the error entirely.
          body = { title: response.statusText };
        }
        // Idempotent replays are surfaced so a caller can tell a genuine
        // conflict from its own retry landing twice.
        const replayed =
          response.headers.get("Idempotency-Replayed") === "true";
        const apiError = new ApiError(body, response.status, replayed);
        applySessionPolicy(apiError, error.request);
        return apiError as unknown as typeof error;
      },
    ],
  },
});

/**
 * Add camelCase aliases beside every snake_case key, without removing anything.
 *
 * WHY
 *
 * The API answers in snake_case — employee_no, first_name, invoice_date — and
 * most pages were written against camelCase. The contract audit never caught
 * it because it checks PATHS: every call resolves to a real route and it
 * reports zero gaps, while the Employee Directory renders rows of em dashes.
 * A path that resolves and a payload that matches are different claims.
 *
 * Both spellings are exposed rather than converting, because pages are split:
 * some read `expected_value`, others read `expectedValue`, and a one-way
 * rename would fix half the screens by breaking the other half. Widening is
 * safe; renaming is not.
 *
 * The original keys are written first so an existing camelCase key from the
 * API always wins over a generated alias, and nothing that works today
 * changes.
 */
function withCamelAliases<T>(value: T, depth = 0): T {
  // Guards runaway recursion on a cyclic or pathologically nested payload;
  // no API response here is anywhere near this deep.
  if (depth > 8 || value === null || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((v) => withCamelAliases(v, depth + 1)) as unknown as T;
  }

  const source = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(source)) {
    out[key] = withCamelAliases(raw, depth + 1);
  }
  for (const [key, raw] of Object.entries(source)) {
    if (!key.includes("_")) continue;
    const camel = key.replace(/_([a-z0-9])/g, (_m, c: string) =>
      c.toUpperCase(),
    );
    // Never clobber a key the API actually sent.
    if (camel !== key && !(camel in source)) {
      out[camel] = withCamelAliases(raw, depth + 1);
    }
  }
  return out as unknown as T;
}

interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

/** Typed GET that unwraps the envelope. */
export async function get<T>(path: string, options?: Options): Promise<T> {
  const response = await client.get(toRequestPath(path), options);
  const body = (await response.json()) as Envelope<T>;
  return withCamelAliases(body.data);
}

/** Typed POST (mutations may carry idempotency keys). */
export async function post<T>(
  path: string,
  body?: unknown,
  idempotencyKey?: string,
  options?: Options,
): Promise<T> {
  if (idempotencyKey)
    useAuthStore.getState().setPendingIdempotencyKey(idempotencyKey);
  const response = await client.post(toRequestPath(path), {
    json: body ?? {},
    ...options,
  });
  const parsed = (await response.json()) as Envelope<T>;
  return parsed.data;
}

/** Typed PATCH with If-Match optimistic concurrency. */
export async function patch<T>(
  path: string,
  body: unknown,
  ifMatch?: string | number,
  options?: Options,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (ifMatch !== undefined) headers["If-Match"] = String(ifMatch);
  const response = await client.patch(toRequestPath(path), {
    json: body,
    headers,
    ...options,
  });
  const parsed = (await response.json()) as Envelope<T>;
  return parsed.data;
}

/** Typed DELETE; 204 responses resolve to null. */
export async function remove<T = null>(
  path: string,
  options?: Options,
): Promise<T> {
  const response = await client.delete(toRequestPath(path), options);
  if (response.status === 204) return null as T;
  const parsed = (await response.json()) as Envelope<T>;
  return parsed.data;
}

/** Raw access for streaming/SSE-free payloads that need response details. */
export const rawClient = client;

/** Server-side error rethrow helper for TanStack Query. */
export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  return new ApiError(
    { code: "NETWORK_ERROR", title: "Network error", detail: String(err) },
    0,
  );
}

export async function list<T>(
  path: string,
  options?: Options,
): Promise<{
  items: T[];
  total: number;
  cursor?: string;
  meta?: Record<string, unknown>;
}> {
  const response = await client.get(toRequestPath(path), options);
  const body = (await response.json()) as Envelope<T[]> & {
    meta?: { total?: number; cursor?: string } & Record<string, unknown>;
  };
  return {
    items: withCamelAliases(body.data ?? []),
    total: body.meta?.total ?? 0,
    cursor: body.meta?.cursor,
    // The rest of the list's meta (a trial balance's totals and currency),
    // as sent.
    meta: body.meta,
  };
}
